/** Clasa de execuție a fiecărui element, gata de citit după id-ul lui de plan.
 *
 * Pasul 19 numără câte tipuri DIFERITE de stâlp, de grindă și de grindă de
 * fundare are soluția (v. `structure/clase.py`): elementele identice — aceeași
 * secțiune, aceeași formă, aceeași armare, aceiași etrieri — sunt o clasă, cu
 * eticheta ei (SA, SB, … la stâlpi; BA, BB, … la grinzi; FA, FB, … la
 * fundație). Aici lista de clase se întoarce pe dos: din `clasă → membri` în
 * `element → clasa lui`, ca orice ecran care arată un element să poată spune
 * și din ce tip face parte.
 *
 * Cheia e id-ul de plan CU nivel («ST8.2»), exact cum îl scrie modelul.
 */

export type ClasaElement = {
  /** eticheta scurtă: SA, BC, FB — goală când piesa n-are încă o clasă de
   *  execuție, dar stă într-un grup de simetrie */
  clasa: string;
  /** câte elemente identice are clasa, numărând și acesta */
  nr: number;
  /** ce le face identice: «30×100 cm · 18Ø18 · etrieri Ø8/7,5–15 cm» */
  eticheta: string;
  /** grupul cu POTENȚIAL de simetrie în care stă piesa (numai stâlpii):
   *  cine ar trebui să fie la fel ca ea — perechea ei oglindită față de
   *  axele clădirii și rândul fațadei la pas egal (`geometrie.clasele_de_
   *  simetrie`) — și cât din grup a ieșit chiar în aceeași clasă de
   *  execuție (`clase.clase_simetrie`). `respectata` e verdictul pragului
   *  din scor; null când armarea n-a rulat încă. */
  simetrie?: {
    grup: string;
    nr: number;
    identice: number | null;
    scor: number | null;
    respectata: boolean | null;
  };
};

import { tn, t } from "../i18n";

const FAMILII = ["stalpi", "grinzi", "fundatie"] as const;

export function indexClase(
  rezultatPas19: unknown,
): Record<string, ClasaElement> {
  const out: Record<string, ClasaElement> = {};
  const clase = (rezultatPas19 as { clase?: Record<string, unknown> } | null)
    ?.clase;
  if (!clase) return out;
  for (const familie of FAMILII) {
    const lista = clase[familie];
    if (!Array.isArray(lista)) continue;
    for (const c of lista as Array<{
      clasa?: string; nr?: number; eticheta?: string; membri?: string[];
    }>) {
      if (!c?.clasa) continue;
      for (const membru of c.membri ?? []) {
        out[membru] = {
          clasa: c.clasa,
          nr: c.nr ?? (c.membri?.length ?? 1),
          eticheta: c.eticheta ?? "",
        };
      }
    }
  }
  const grupuri = (clase.simetrie as { grupuri?: unknown } | undefined)?.grupuri;
  if (Array.isArray(grupuri)) {
    for (const g of grupuri as Array<{
      id?: string; membri?: string[]; identice_executie?: number | null;
      scor?: number | null; respectata?: boolean | null;
    }>) {
      if (!g?.id) continue;
      for (const membru of g.membri ?? []) {
        out[membru] = {
          ...(out[membru] ?? { clasa: "", nr: 0, eticheta: "" }),
          simetrie: {
            grup: g.id,
            nr: g.membri?.length ?? 0,
            identice: g.identice_executie ?? null,
            scor: g.scor ?? null,
            respectata: g.respectata ?? null,
          },
        };
      }
    }
  }
  return out;
}

/** «SA · 23 elemente identice» / «SA · singurul de felul lui». */
export function descrieClasa(c: ClasaElement | undefined): string | null {
  if (!c || !c.clasa) return null;
  return c.nr > 1
    ? tn("struct.clasa.identice#n", c.nr, { clasa: c.clasa })
    : t("struct.clasa.singurul", { clasa: c.clasa });
}

/** «G2 · 8 din 10 identici» / «G2 · 10 stâlpi, încă nemăsurat». */
export function descrieSimetria(c: ClasaElement | undefined): string | null {
  const s = c?.simetrie;
  if (!s) return null;
  return s.identice == null
    ? t("struct.simetrie.nemasurat", { grup: s.grup, n: s.nr })
    : t("struct.simetrie.identici", { grup: s.grup, k: s.identice, n: s.nr });
}
